import { HATAG } from '@business/constants/helparticle';
import { UserRole } from '@business/entities/account';
import { AppContext, GCApp } from '@core/components/AppContextProvider';
import { PrivateRoute, Redirect, Router } from '@core/router';
import { Session } from '@modules/auth/session';
import HelpArticlePage from '@modules/helpArticles/HelpArticlePage';
import HelpArticlesPage from '@modules/helpArticles/HelpArticlesPage';
import CheckListDetailPage from '@modules/marketing/CheckListDetailPage';
import TodosPage from '@modules/TodosPage';
import React, { lazy, memo, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';
import { ViewProposalPage } from '@modules/proposals/ProposalsPage/ViewProposalPage';
import { PermissionService } from '@business/services/permission_service';
import { Switch } from 'react-router-dom';
import { Config } from '@config';

// import AddEditTMChangeOrderPage from '@modules/timeMaterials/AddEditTMChangeOrderPage';

const ReportsPage = lazy(() => import('@modules/reports'));
const AdvancedReportingPage = lazy(() => import('@modules/reports/AdvancedReportingPage'));
const CompanyOverviewPage = lazy(() => import('@modules/CompanyOverviewPage'));
const MediaPage = lazy(() => import('@modules/media'));
const CompaniesPage = lazy(() => import('@modules/companies/CompaniesPage'));
const CompanyPage = lazy(() => import('@modules/companies/CompanyPage'));
const OrganizationsPage = lazy(() => import('@modules/organizations/OrganizationsPage'));
const OrganizationPage = lazy(() => import('@modules/organizations/OrganizationPage'));
const ContactsPage = lazy(() => import('@modules/people/ContactsPage'));
const ContactPage = lazy(() => import('@modules/people/ContactPage'));
const SettingsPage = lazy(() => import('@modules/settings/SettingsPage'));
const MyAccountPage = lazy(() => import('@modules/account/MyAccountPage'));
const UsersPage = lazy(() => import('@modules/users/UsersPage'));
const ActivityLogPage = lazy(() => import('@modules/activities/ActivityLogPage'));
const UserRolesPage = lazy(() => import('@modules/user-roles/UserRolesPage'));
const UserRolePermissionPage = lazy(() => import('@modules/user-roles/UserRolePermissionPage'));
const CreateProjectPage = lazy(() => import('@modules/projects/CreateProjectPage'));
const CreateSimpleProjectPage = lazy(() => import('@modules/projects/CreateProjectPage/CreateSimpleProjectPage'));
const EditProjectPage = lazy(() => import('@modules/projects/EditProjectPage'));
const CreateSubscriptionPage = lazy(() => import('@modules/subscription/CreateSubscriptionPage'));
const SubOneTimePayment = lazy(() => import('@modules/subscription/SubOneTimePayment'));
const EditSubscription = lazy(() => import('@modules/subscription/EditSubscription'));
const BoardPage = lazy(() => import('@modules/board/BoardPage'));
const ProjectTemplatesPage = lazy(() => import('@modules/templates/TemplatesPage'));
const TemplatePage = lazy(() => import('@modules/templates/TemplatePage'));
const MarketingBoardPageV2 = lazy(() => import('@modules/MarketingV2/MarketingBoardPage'));
const CheckListPage = lazy(() => import('@modules/marketing/CheckListPage'));
const TicketsPage = lazy(() => import('@modules/support/TicketsPage'));
const TicketDetailPage = lazy(() => import('@modules/support/TicketDetailPage'));
const MyTicketsPage = lazy(() => import('@modules/support/MyTicketsPage'));
const SupportCategoryPage = lazy(() => import('@modules/support/CategoryPage'));
const DealBoardPage = lazy(() => import('@modules/dealBoard/DealBoardPage'));
const DealPage = lazy(() => import('@modules/deals/DealPage'));
const FocusModePage = lazy(() => import('@modules/focusMode/FocusModePage'));
const TeamsPage = lazy(() => import('@modules/teams/TeamsPage'));
const ClientProjects = lazy(() => import('@modules/clientProjects'));
const ClientProject = lazy(() => import('@modules/clientProjects/clientProject'));
const DashboardPage = lazy(() => import('@modules/dashboards/DashboardPage'));
const FormPage = lazy(() => import('@modules/public/FormPage'));
const ClientFormPage = lazy(() => import('@modules/public/ClientFormPage'));
const ClientForms = lazy(() => import('@modules/ClientForms'));
const ReviewsPage = lazy(() => import('@modules/reviews'));
const ClientMediaPage = lazy(() => import('@modules/ClientMedia'));
const BrandDetailPage = lazy(() => import('@modules/companies/CompanyPage/BrandDetailPage'));
const LibraryPage = lazy(() => import('@modules/LibraryPage'));
const ChecklistsPage = lazy(() => import('@modules/todoTemplates/ChecklistsPage'));
const ChecklistTemplatePage = lazy(() => import('@modules/todoTemplates/ChecklistTemplatePage'));
const CoursesPage = lazy(() => import('@modules/course/CoursesPage'));
const CoursePage = lazy(() => import('@modules/course/CoursePage'));
const ClientMyCoursePage = lazy(() => import('@modules/course/ClientMyCoursePage'));
const ManageCoursePage = lazy(() => import('@modules/course/ManageCoursePage'));
const TasksPage = lazy(() => import('@modules/tasks/TasksPage'));
const OrdersPage = lazy(() => import('@modules/orders/OrdersPage'));
const AddEditOrderPage = lazy(() => import('@modules/orders/AddEditOrderPage'));
const VendorsPage = lazy(() => import('@modules/orders/VendorsPage'));
const ProductsPage = lazy(() => import('@modules/orders/ProductsPage'));
const PurchaseOrdersPage = lazy(() => import('@modules/orders/PurchaseOrdersPage'));
const IdeasPage = lazy(() => import('@modules/ideas/IdeasPage'));
const IdeaPage = lazy(() => import('@modules/ideas/IdeaPage'));
const EmptyPage = lazy(() => import('@modules/empty/EmptyPage'));
const SetEmailPage = lazy(() => import('@modules/account/SetEmailPage'));
const AddEditOrderInvoicePage = lazy(() => import('@modules/orders/AddEditOrderInvoicePage'));
const EditPOPage = lazy(() => import('@modules/orders/EditPOPage'));
const POLadingPage = lazy(() => import('@modules/orders/POLadingPage/POLadingPage'));
const POPickListPage = lazy(() => import('@modules/orders/POLadingPage/POPickListPage'));
const POLadingPageTM = lazy(() => import('@modules/timeMaterials/POLadingPage/POLadingPage'));
const POPickListPageTM = lazy(() => import('@modules/timeMaterials/POLadingPage/POPickListPage'));
const SharedProjectsPage = lazy(() => import('@modules/sharedProjects'));
const GlossaryPage = lazy(() => import('@modules/course/GlossaryPage'));
const LineUpPage = lazy(() => import('@modules/course/LineUpPage'));
const LineUpDetailPage = lazy(() => import('@modules/course/LineUpPage/LineUpDetailPage'));
const DynamicReportPage = lazy(() => import('@modules/dynamicReports/DynamicReportPage'));
const ChangelogsPage = lazy(() => import('@modules/changelogs/ChangelogsPage'));
const PreviewCoursePage = lazy(() => import('@modules/course/CoursePage/PreviewCoursePage'));
const CommissionsPage = lazy(() => import('@modules/commissions'));
const ProposalBuilderPage = lazy(() => import('@modules/proposal/ProposalBuilderPage'));
const ProposalTemplatePage = lazy(() => import('@modules/proposalTemplates/ProposalTemplatePage'));
const UniversalSectionsPage = lazy(() => import('@modules/UniversalSections/UniversalSectionsPage'));
const SharedProjectBoardPage = lazy(() => import('@modules/board/BoardPage/SharedProjectBoardPage'));
const ShareProjectBoardPage = lazy(() => import('@modules/board/BoardPage/ShareProjectBoardPage'));
const ProposalsPage = lazy(() => import('@modules/proposals/ProposalsPage'));
const ProposalActivitiesPage = lazy(() => import('@modules/proposals/ProposalActivitiesPage'));
// const ProposalsPreviewPage = lazy(() => import('@modules/proposals/ViewProposalModal/ProposalPreviewPage'));
const CourseBuilderPage = lazy(() => import('@modules/course/CourseBuilder/CourseBuilderPage'));
const RedirectTodosPage = lazy(() => import('@modules/RedirectTodosPage'));
const PackagesPage = lazy(() => import('@modules/orders/PackagesPage'));
const PackagePage = lazy(() => import('@modules/orders/PackagePage'));
const TimeMaterialsPage = lazy(() => import('@modules/timeMaterials/TimeMaterialsPage'));
const AddEditTimeMaterialPage = lazy(() => import('@modules/timeMaterials/AddEditTimeMaterialPage'));
const AddEditTimeMaterialInvoicePage = lazy(() => import('@modules/timeMaterials/AddEditTimeMaterialInvoicePage'));
const FormsPage = lazy(() => import('@modules/Forms/FormsPage'));
const ManagementFormTemplatesPage = lazy(() => import('@modules/Forms/ManagementFormTemplatesPage'));
const OpenEmailLinkPage = lazy(() => import('@modules/OpenEmailLinkPage'));
const OpenProjectLinkPage = lazy(() => import('@modules/OpenProjectLinkPage'));
const ClientHomePage = lazy(() => import('@modules/ClientHomePage'));
const VendastaAccountsPage = lazy(() => import('@modules/organizations/VendastaAccountsPage'));
const VendastaBillingReportPage = lazy(() => import('@modules/organizations/VendastaBillingReportPage'));
const HomeLandingPage = lazy(() => import('@modules/dashboard/HomeLandingPage'));
const HomePage = lazy(() => import('@modules/home'));
const RedirectPage = lazy(() => import('@modules/empty/RedirectPage'));
const StudentsPage = lazy(() => import('@modules/Students'));
const StudentCoursesPage = lazy(() => import('@modules/Students/CoursesPage'));
const StudentCoursesPageV2 = lazy(() => import('@modules/Students/CoursesPageV2'));
const BrandsPage = lazy(() => import('@modules/orders/BrandsPage'));
const CourseInboxPage = lazy(() => import('@modules/CourseInboxPage'));
const CourseInboxReplyPage = lazy(() => import('@modules/CourseInboxPage/CourseInboxReplyPage'));
const CourseInboxReplyEntry = lazy(() => import('@modules/CourseInboxPage/CourseInboxReplyPage/CourseInboxReplyEntry'));
const ClientCourseMessagePage = lazy(() => import('@modules/ClientCourseMessagePage'));
const TimeOffPage = lazy(() => import('@modules/timeoff/TimeOffPage'));
const AutomationPage = lazy(() => import('@modules/automation/AutomationPage'));
const AutomationsPage = lazy(() => import('@modules/automation/AutomationsPage'));
const PresentationsPage = lazy(() => import('@modules/presentation/PresentationsPage'));
const PresentationPage = lazy(() => import('@modules/presentation/PresentationPage'));
const NotificationsPage = lazy(() => import('@modules/notifications'));
const PresentationBuilderPage = lazy(() => import('@modules/presentation/PresentationPage/PresentationBuilder/PresentationBuilderPage'));
const AddEditProposalsPage = lazy(() => import('@modules/proposals/AddEditProposalPage'));
const OnboardingPage = lazy(() => import('@modules/onboarding'));
const ChangeOrderSubscription = lazy(() => import('@modules/subscription/ChangeOrderSubscription'));
const DevTestsPage = lazy(() => import('@modules/devtests/DevTestsPage'));
const AIDashboardFeedPage = lazy(() => import('@modules/aiDashboard/AIDashboardFeedPage'));
const AIDashboardProgressPage = lazy(() => import('@modules/aiDashboard/AIDashboardProgressPage'));

export const HelpArticleTags$ = new BehaviorSubject<HATAG[]>([]);

export const DashboardRouter = memo(() => {
  return (
    <Router>
      <AppContext.Provider value={GCApp.agency}>
        <Switch>
          <PrivateRoute
            exact
            path="/proposal-builder/:id"
            component={ProposalBuilderPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Proposal']}
          />

          <PrivateRoute
            exact
            path="/proposal-templates/:folderId?"
            component={ProposalTemplatePage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Proposal']}
          />
          <PrivateRoute
            exact
            path="/universal-sections"
            component={UniversalSectionsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Proposal']}
          />
          <PrivateRoute
            exact
            path="/proposal-activities"
            component={ProposalActivitiesPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Proposal']}
          />
          <PrivateRoute
            exact
            path={['/proposals', '/proposals/:id', '/proposals/:status/:id', '/proposals/:id/:tab/view']}
            component={ProposalsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Proposal']}
          />
          <PrivateRoute
            exact
            path={['/add-edit-proposal', '/add-edit-proposal/:id']}
            component={AddEditProposalsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Proposal']}
          />
          <PrivateRoute exact path="/proposals/:id" component={ViewProposalPage} roles={[UserRole.admin, UserRole.user]} tags={['Proposal']} />

          <PrivateRoute exact path="/companies" component={CompaniesPage} roles={[UserRole.admin, UserRole.user]} tags={['Company', 'Brand']} />
          <PrivateRoute exact path="/companies/:id" component={CompanyPage} roles={[UserRole.admin, UserRole.user]} tags={['Company', 'Brand']} />
          <PrivateRoute
            exact
            path="/companies/brand/:id"
            component={BrandDetailPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Company', 'Brand']}
          />

          <PrivateRoute
            exact
            path="/organizations"
            component={OrganizationsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Organization', 'Contacts']}
          />
          <PrivateRoute
            exact
            path="/organizations/:id/:tab?"
            component={OrganizationPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Organization', 'Contacts']}
          />
          <PrivateRoute
            exact
            path="/vendasta-accounts"
            component={VendastaAccountsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Organization', 'Contacts']}
          />
          <PrivateRoute
            exact
            path="/vendasta-billing-report"
            component={VendastaBillingReportPage}
            roles={[UserRole.admin]}
            tags={['Organization', 'Contacts']}
          />

          <PrivateRoute exact path="/people" component={ContactsPage} roles={[UserRole.admin, UserRole.user]} tags={['Organization', 'Contacts']} />
          <PrivateRoute
            exact
            path="/people/:id"
            component={ContactPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Organization', 'Contacts']}
          />

          <PrivateRoute
            exact
            path="/settings"
            component={SettingsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Settings', 'Integrations', 'Permissions', 'Webhook', 'Import', 'Forms']}
          />
          <PrivateRoute
            exact
            path="/settings/:tab"
            component={SettingsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Settings', 'Integrations', 'Permissions', 'Webhook', 'Import', 'Forms']}
          />
          <PrivateRoute
            exact
            path="/settings/:tab/:subTab"
            component={SettingsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Settings', 'Integrations', 'Permissions', 'Webhook', 'Import', 'Forms']}
          />
          <PrivateRoute exact path="/my-account" component={MyAccountPage} tags={['Settings']} />
          <PrivateRoute exact path="/users" component={UsersPage} roles={[UserRole.admin, UserRole.user]} tags={['Users']} />
          <PrivateRoute exact path="/activity-logs" component={ActivityLogPage} roles={[UserRole.admin, UserRole.user]} tags={['Activity Log']} />
          <PrivateRoute exact path="/home-landing" component={HomeLandingPage} roles={[UserRole.admin, UserRole.user]} tags={['Home']} />

          <PrivateRoute exact path="/user-roles" component={UserRolesPage} roles={[UserRole.admin, UserRole.user]} tags={['Users', 'Permissions']} />
          <PrivateRoute
            exact
            path="/user-roles/permission/:id"
            component={UserRolePermissionPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Users', 'Permissions']}
          />

          {/* Projects */}
          <PrivateRoute
            exact
            path="/projects/create-project"
            component={CreateProjectPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Project']}
          />
          <PrivateRoute
            exact
            path="/projects/create-project/:id"
            component={CreateProjectPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Project']}
          />
          <PrivateRoute
            exact
            path="/projects/create-project/:id/:isProject"
            component={CreateSimpleProjectPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Project']}
          />
          <PrivateRoute
            exact
            path="/projects/edit/:orgId/:id"
            component={EditProjectPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Project']}
          />
          <PrivateRoute
            exact
            path="/subscriptions/create-subscription/:id"
            component={CreateSubscriptionPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Subscription']}
          />
          <PrivateRoute
            exact
            path="/subscriptions/one-time-payment/:orgId/:id"
            component={SubOneTimePayment}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Subscription']}
          />
          <PrivateRoute
            exact
            path="/subscriptions/edit/:orgId/:id"
            component={EditSubscription}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Subscription']}
          />

          <PrivateRoute
            exact
            path="/subscriptions/change/:orgId/:id"
            component={ChangeOrderSubscription}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Subscription']}
          />

          <PrivateRoute
            exact
            path={['/board/shared/project/:id']}
            component={SharedProjectBoardPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Project', 'Contacts', 'Billing']}
          />

          <PrivateRoute
            exact
            path={['/board', '/board/project/:id', '/board/project/:id/task/:taskId', '/board/project/:id/task/:taskId/subtask/:subtaskId']}
            component={BoardPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Project', 'Contacts', 'Billing']}
          />
          <PrivateRoute
            exact
            path={['/shared-projects', '/shared-projects/:id']}
            component={SharedProjectsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Project', 'Contacts', 'Billing']}
          />
          <PrivateRoute
            exact
            path="/project-template/:folderId?"
            component={ProjectTemplatesPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Project']}
          />

          <PrivateRoute
            exact
            path="/project-template/detail/:id"
            component={TemplatePage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Project']}
          />

          <PrivateRoute
            exact
            path="/subscription-board/checklist/:folderId?"
            component={CheckListPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Organization', 'Subscription']}
          />
          <PrivateRoute
            exact
            path="/subscription-board/checklist/detail/:id"
            component={CheckListDetailPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Organization', 'Subscription']}
          />
          <PrivateRoute exact path="/dev-tests" component={DevTestsPage} roles={[UserRole.admin, UserRole.user]} />

          {/* Marketing */}

          {/* <PrivateRoute
            exact
            path={['/marketing-board', '/marketing-board/board/:id/:boardSlug?', '/marketing-board/:boardSlug?']}
            component={MarketingBoardPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Organization', 'Subscription']}
          /> */}

          <PrivateRoute
            exact
            path={[
              '/subscription-board',
              '/subscription-board/board/:id',
              '/subscription-board/board/:id/:type/:checklistId/:recurringChecklistId/task/:taskId',
              '/subscription-board/board/:id/:type/:checklistId/:recurringChecklistId/task/:taskId/subtask/:subtaskId',
            ]}
            component={MarketingBoardPageV2}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Organization', 'Subscription']}
          />

          {/* Support */}
          <PrivateRoute exact path="/support-tickets" component={TicketsPage} roles={[UserRole.admin, UserRole.user]} tags={['Support']} />
          <PrivateRoute
            exact
            path={[
              '/support-tickets/detail/:id',
              '/support-tickets/detail/:id/task/:taskId',
              '/support-tickets/detail/:id/task/:taskId/subtask/:subtaskId',
            ]}
            component={TicketDetailPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Support']}
          />
          <PrivateRoute
            exact
            path="/support-tickets/my-tickets"
            component={MyTicketsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Support']}
          />
          <PrivateRoute
            exact
            path="/support-tickets/category"
            component={SupportCategoryPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Support']}
          />
          <PrivateRoute exact path="/support-tickets/:tab" component={TicketsPage} roles={[UserRole.admin, UserRole.user]} tags={['Support']} />

          {/* Deals */}
          {/* Declared before /deals/:id so "focus" is never read as a deal id. */}
          <PrivateRoute exact path="/deals/focus" component={FocusModePage} roles={[UserRole.admin, UserRole.user]} tags={['Deals', 'Contacts']} />
          <PrivateRoute
            exact
            path="/deals/:id?/:orgId?"
            component={DealBoardPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Deals', 'Contacts']}
          />
          <PrivateRoute exact path="/deals/:id/:tab?" component={DealPage} roles={[UserRole.admin, UserRole.user]} tags={['Deals', 'Contacts']} />

          <PrivateRoute exact path="/help-articles/:id?" component={HelpArticlesPage} roles={[UserRole.admin, UserRole.user]} />
          <PrivateRoute exact path="/help-article/category/:id" component={HelpArticlesPage} roles={[UserRole.admin, UserRole.user]} />

          <PrivateRoute exact path="/help-article/:id" component={HelpArticlePage} roles={[UserRole.admin, UserRole.user]} />

          <PrivateRoute
            exact
            path={['/advanced-reporting', '/advanced-reporting/:id']}
            component={AdvancedReportingPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Reports']}
          />

          <PrivateRoute
            exact
            path="/reports-2/:moduleId?/:reportId?"
            component={DynamicReportPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Reports']}
          />

          <PrivateRoute
            exact
            path="/reports/:moduleId?/:reportId?"
            component={ReportsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Reports']}
          />
          <PrivateRoute exact path="/company-overview" component={CompanyOverviewPage} roles={[UserRole.admin, UserRole.user]} tags={['Reports']} />

          <PrivateRoute exact path="/media" component={MediaPage} roles={[UserRole.admin, UserRole.user]} tags={['Media']} />
          <PrivateRoute exact path="/media/:id" component={MediaPage} roles={[UserRole.admin, UserRole.user]} tags={['Media']} />

          <PrivateRoute exact path="/teams" component={TeamsPage} roles={[UserRole.admin, UserRole.user]} tags={['Teams']} />

          <PrivateRoute exact path="/dashboard/:slug?" component={DashboardPage} roles={[UserRole.admin, UserRole.user]} tags={['Dashboard']} />

          <PrivateRoute exact path="/projects" component={ClientProjects} roles={[UserRole.client]} tags={['Project']} />
          <PrivateRoute exact path="/project/:id/:appId?" component={ClientProject} roles={[UserRole.client]} tags={['Project']} />

          <PrivateRoute
            exact
            path={['/client/students/courses/:id', '/client/my-courses']}
            component={StudentCoursesPage}
            roles={[UserRole.client]}
          />

          <PrivateRoute exact path={'/client/courses/view/:id'} component={CoursePage} roles={[UserRole.client]} />
          <PrivateRoute exact path={'/client/my-courses/view/:id'} component={ClientMyCoursePage} roles={[UserRole.client]} />
          <PrivateRoute exact path={'/client/inbox/:id'} component={ClientCourseMessagePage} roles={[UserRole.client]} tags={['Training']} />

          <PrivateRoute exact path="/client-home" component={ClientHomePage} roles={[UserRole.client]} tags={['Home']} />
          <PrivateRoute exact path="/home" component={HomePage} roles={[UserRole.admin, UserRole.user]} tags={['Home']} />

          <PrivateRoute exact path="/forms" component={ClientForms} roles={[UserRole.client]} tags={['Settings', 'Forms']} />
          <PrivateRoute exact path="/form/:fileId" component={FormPage} roles={[UserRole.client]} tags={['Settings', 'Forms']} />
          <PrivateRoute exact path="/client-form/:fileId" component={ClientFormPage} roles={[UserRole.client]} tags={['Settings', 'Forms']} />
          <PrivateRoute exact path="/files" component={ClientMediaPage} roles={[UserRole.client]} tags={['Files']} />
          <PrivateRoute exact path="/files/detail/:id" component={ClientMediaPage} roles={[UserRole.client]} tags={['Files']} />

          {/* Changelogs */}

          <PrivateRoute exact path="/changelogs" component={ChangelogsPage} roles={[UserRole.admin, UserRole.user]} />

          <PrivateRoute exact path="/tasks/:type?" component={TasksPage} roles={[UserRole.admin, UserRole.user]} tags={['To-Dos']} />

          <PrivateRoute exact path="/ideas" component={IdeasPage} roles={[UserRole.admin, UserRole.user]} />
          <PrivateRoute exact path="/idea/:id" component={IdeaPage} roles={[UserRole.admin, UserRole.user]} />

          {/* Orders */}
          <PrivateRoute exact path="/order/edit/:orgId/:id?" component={AddEditOrderPage} roles={[UserRole.admin, UserRole.user]} />
          <PrivateRoute
            exact
            path="/time-material/edit/:orgId/:id?"
            component={AddEditTimeMaterialPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute
            exact
            path="/order/invoice/:orgId/:orderId/:invoiceId?"
            component={AddEditOrderInvoicePage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute
            exact
            path={['/order/po/:orderId/:poId/:type?', '/time-material/po/:orderId/:poId/:type?']}
            component={EditPOPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute
            exact
            path="/order/po-lading/:orderId/:poId"
            component={POLadingPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute
            exact
            path="/order/po-picklist/:orderId/:poId"
            component={POPickListPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute
            exact
            path="/time-material/po-lading/:orderId/:poId"
            component={POLadingPageTM}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute
            exact
            path="/time-material/po-picklist/:orderId/:poId"
            component={POPickListPageTM}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute exact path={['/orders', '/orders/detail/:id']} component={OrdersPage} roles={[UserRole.admin, UserRole.user]} />
          <PrivateRoute exact path="/orders/create/:orgId" component={AddEditOrderPage} roles={[UserRole.admin, UserRole.user]} />

          <PrivateRoute
            exact
            path="/time-materials/create/:orgId"
            component={AddEditTimeMaterialPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute exact path="/notifications" component={NotificationsPage} roles={[UserRole.admin, UserRole.user]} />

          <PrivateRoute
            exact
            path={['/time-materials', '/time-materials/detail/:id']}
            component={TimeMaterialsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />

          <PrivateRoute
            exact
            path="/time-material/edit/:orgId/:id?"
            component={AddEditTimeMaterialPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute
            exact
            path="/time-material/invoice/:orgId/:orderId/:boardId"
            component={AddEditTimeMaterialInvoicePage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />

          {/* <PrivateRoute exact path="/time-material/change-order/:tmId" component={AddEditTMChangeOrderPage} roles={[UserRole.admin, UserRole.user]} /> */}

          <PrivateRoute
            exact
            path={['/orders/suppliers', '/time-material/suppliers']}
            component={VendorsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute
            exact
            path={['/orders/brands', '/time-material/brands']}
            component={BrandsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute
            exact
            path={['/orders/items', '/orders/items/:itemId', '/time-material/items', '/time-material/items/:itemId']}
            component={ProductsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute
            exact
            path={['/orders/purchase-orders', '/time-material/purchase-orders']}
            component={PurchaseOrdersPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />

          <PrivateRoute exact path="/report-builder" component={EmptyPage} roles={[UserRole.admin, UserRole.user]} />
          <PrivateRoute exact path="/set-email" component={SetEmailPage} roles={[UserRole.admin, UserRole.user]} />
          <PrivateRoute exact path="/commissions" component={CommissionsPage} roles={[UserRole.admin, UserRole.user]} tags={['Commission']} />
          {/* <PrivateRoute exact path="/clock-ins" component={ClockinReports} roles={[UserRole.admin, UserRole.user]} /> */}

          <PrivateRoute
            exact
            path={['/orders/packages', '/time-material/packages']}
            component={PackagesPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute
            exact
            path={['/orders/package/:id', '/time-material/package/:id']}
            component={PackagePage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Time Material']}
          />
          <PrivateRoute exact path="/redirect/:id?" component={RedirectPage} roles={[UserRole.admin, UserRole.user]} />

          <PrivateRoute exact path="/automations" component={AutomationsPage} roles={[UserRole.admin, UserRole.user]} tags={['Automation']} />
          <PrivateRoute exact path="/automations/:id" component={AutomationPage} roles={[UserRole.admin, UserRole.user]} tags={['Automation']} />

          <PrivateRoute exact path="/presentations" component={PresentationsPage} roles={[UserRole.admin, UserRole.user]} tags={['Presentation']} />
          <PrivateRoute
            exact
            path="/presentations/:id"
            component={PresentationPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Presentation']}
          />
          <PrivateRoute exact path="/reviews" component={ReviewsPage} roles={[UserRole.admin]} tags={['Reviews']} />

          <Redirect
            exact
            from="/"
            to={
              Session.user && (Session.user.role === UserRole.admin || Session.user.role === UserRole.user)
                ? PermissionService.canSeeIt(Session.user, 'organizations')
                  ? '/home'
                  : '/home-landing'
                : '/client-home'
            }
          />
          {/* <Route path="*" exact component={NotFroundPage} /> */}
        </Switch>
      </AppContext.Provider>
      <AppContext.Provider value={GCApp.management}>
        <Switch>
          <PrivateRoute
            exact
            path={['/library', '/library/:id', '/library/:id/:mode/:type/:itemId']}
            component={LibraryPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Library']}
          />
          {/* <PrivateRoute exact path={['/board', '/board/project/:id']} component={BoardPage} roles={[UserRole.admin, UserRole.user]} /> */}
          <PrivateRoute
            exact
            path="/checklists/:type?"
            component={ChecklistsPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['To-Dos', 'Checklists']}
          />
          <PrivateRoute
            exact
            path="/checklists/:slug/:type/:id"
            component={ChecklistTemplatePage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['To-Dos', 'Checklists']}
          />
          {/* New courses section */}
          <PrivateRoute exact path="/course-builder/:id" component={CourseBuilderPage} roles={[UserRole.admin, UserRole.user]} tags={['Training']} />
          <PrivateRoute
            exact
            path={['/courses', '/courses/:folderId']}
            component={CoursesPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Training']}
          />
          <PrivateRoute
            exact
            path="/courses/manage/:id/:type?"
            component={ManageCoursePage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Training']}
          />
          <PrivateRoute exact path="/students" component={StudentsPage} roles={[UserRole.admin, UserRole.user]} tags={['Training']} />
          <PrivateRoute exact path={['/inbox']} component={CourseInboxPage} roles={[UserRole.admin, UserRole.user]} tags={['Training']} />
          <PrivateRoute exact path={['/inbox/:id/:type']} component={CourseInboxReplyEntry} tags={['Training']} />
          {/* <PrivateRoute
            exact
            path={['/instructor-inbox', '/student-inbox']}
            component={CourseInboxPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Training']}
          />
          <PrivateRoute
            exact
            path={['/instructor-inbox/:id', '/student-inbox/:id']}
            component={CourseInboxReplyPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Training']}
          /> */}
          <PrivateRoute
            exact
            path={['/students/courses/:id']}
            component={StudentCoursesPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Training']}
          />
          <PrivateRoute exact path={['/my-courses']} component={StudentCoursesPageV2} roles={[UserRole.admin, UserRole.user]} tags={['Training']} />
          <PrivateRoute
            exact
            path={['/courses/view/:id', '/my-courses/view/:id']}
            component={CoursePage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Training']}
          />
          <PrivateRoute exact path="/courses/preview/:id" component={PreviewCoursePage} roles={[UserRole.admin, UserRole.user]} tags={['Training']} />
          <PrivateRoute
            exact
            path={['/glossary', '/glossary/:mode/:id']}
            component={GlossaryPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Training']}
          />
          <PrivateRoute exact path="/line-up" component={LineUpPage} roles={[UserRole.admin, UserRole.user]} tags={['Training']} />
          <PrivateRoute exact path="/line-up/:id" component={LineUpDetailPage} roles={[UserRole.admin, UserRole.user]} tags={['Training']} />
          <PrivateRoute exact path="/todo/:appId" component={RedirectTodosPage} roles={[UserRole.admin, UserRole.user]} tags={['To-Dos']} />
          <PrivateRoute exact path="/open-email" component={OpenEmailLinkPage} roles={[UserRole.admin, UserRole.user, UserRole.client]} />
          <PrivateRoute
            exact
            path="/open-project"
            component={OpenProjectLinkPage}
            roles={[UserRole.admin, UserRole.user, UserRole.client]}
            tags={['To-Dos']}
          />
          <PrivateRoute
            exact
            path={['/todos', '/todos/:tab', '/todos/:tab/task/:taskId', '/todos/:tab/checklist/:checklistId']}
            component={TodosPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['To-Dos']}
          />
          <PrivateRoute exact path="/timeoff" component={TimeOffPage} />
          <PrivateRoute exact path="/all-forms/:type?/:id?" component={FormsPage} tags={['Forms']} />
          <PrivateRoute exact path="/management-form-templates" component={ManagementFormTemplatesPage} tags={['Forms']} />
          <PrivateRoute
            exact
            path="/presentation-builder/:presentationId/:stepId"
            component={PresentationBuilderPage}
            roles={[UserRole.admin, UserRole.user]}
            tags={['Presentation']}
          />
          <PrivateRoute exact path="/onboarding" component={OnboardingPage} />
          {/* AI Dashboard Routes */}
          <PrivateRoute exact path="/ai-dashboard/feed" component={AIDashboardFeedPage} />;
          <PrivateRoute exact path="/ai-dashboard/progress" component={AIDashboardProgressPage} />;
          <PrivateRoute exact path="/empty-page" component={EmptyPage} roles={[UserRole.admin, UserRole.user]} />
        </Switch>
      </AppContext.Provider>
    </Router>
  );
});
